import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	signal,
} from '@angular/core';
import { ItemMenuComponent } from '@layout/menu/item-menu/item-menu.component';
import { ThemeService } from '@services/layout/theme.service';
import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { SharedModule } from '@shared/modules/shared.module';

@Component({
	selector: 'app-ui-sidebar',
	imports: [SharedModule, ItemMenuComponent, AvatarComponent, ButtonComponent],
	templateUrl: '../../../../layout/menu/sidebar/sidebar.component.html',
	styleUrl: '../../../../layout/menu/sidebar/sidebar.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiSidebarComponent implements OnInit {
	@Input() currentSection = 'select';
	@Output() sectionChange = new EventEmitter<string>();

	constructor(private themeService: ThemeService) {}

	isSidebarOpen = true;
	showProfileDialog = false;
	isProfileMenuOpen = false;
	currentEmployeeId?: number;
	currentUser: any = null;
	userName: string = 'Usuario';
	userRole: string = '';
	modelNavigation: any[] = [];
	profileMenuItems: any[] = [];

	// Static logo for UI Kit demo
	logo = signal('assets/layout/images/logo.png');

	modelUpper: any[] = [
		{
			label: 'Theme Styles',
			id: 'theme-styles',
			icon: 'Palette',
		},
		{
			label: 'Text Sizes',
			id: 'text-sizes',
			icon: 'PencilRuler',
		},
		{
			label: 'Buttons',
			id: 'buttons',
			icon: 'SquareDashedMousePointer',
		},
		{
			label: 'Input',
			id: 'inputs',
			icon: 'Pencil',
		},
		{
			label: 'Password',
			id: 'password',
			icon: 'Lock',
		},
		{
			label: 'Select',
			id: 'select',
			icon: 'ChevronDown',
		},
		{
			label: 'Multiselect',
			id: 'multiselect',
			icon: 'ChevronDown',
		},
		{
			label: 'Search',
			id: 'search',
			icon: 'Search',
		},
		{
			label: 'Chips',
			id: 'chips',
			icon: 'Hash',
		},
		{
			label: 'Tabs',
			id: 'tabs',
			icon: 'ListCollapse',
		},
		{
			label: 'Tag',
			id: 'tag',
			icon: 'Tag',
		},
		{
			label: 'Icon Status',
			id: 'icon-status',
			icon: 'CircleAlert',
		},
		{
			label: 'Textarea',
			id: 'textarea',
			icon: 'FileText',
		},
		{
			label: 'Datepicker',
			id: 'datepicker',
			icon: 'Calendar',
		},
		{
			label: 'Form Controls',
			id: 'form-controls',
			icon: 'CheckSquare',
		},
		{
			label: 'Loading',
			id: 'loading',
			icon: 'LoaderCircle',
		},
		{
			label: 'Toast',
			id: 'toast',
			icon: 'CircleDashed',
		},
		{
			label: 'Others',
			id: 'others',
			icon: 'LayoutGrid',
		},
		{
			label: 'Not Found (404)',
			id: 'not-found',
			icon: 'FileX',
		},
		{
			label: 'Payment Required',
			id: 'payment-required',
			icon: 'CreditCard',
		},
	];

	modelLower: any[] = [
		{
			type: 'button',
			label: 'Close Panel',
			id: 'isSidebarOpen',
			icon: this.isSidebarOpen ? 'PanelLeftClose' : 'PanelLeftOpen',
			action: () => this.toggleSidebar(),
		},
		{
			id: 'lightMode',
		},
	];

	modelResponsive: any[] = [];
	modelLowerResponsive: any[] = [];
	currentTheme: 'light' | 'dark' = 'light';

	// Unified template renders grouped sections; the UI Kit shows all its demo
	// entries under a single group.
	menuSections: Array<{ id: string; labelKey: string; items: any[] }> = [
		{ id: 'components', labelKey: 'Components', items: this.modelUpper },
	];

	isItemActive(item: { id?: string }): boolean {
		return this.currentSection === item.id;
	}

	openProfileModal(): void {
		// No-op for UI Kit
	}

	toggleSidebar(): void {
		this.isSidebarOpen = !this.isSidebarOpen;
		this.updateLowerModel();
	}

	private updateLowerModel(): void {
		this.modelLower = [
			{
				type: 'button',
				label: 'Close Panel',
				id: 'isSidebarOpen',
				icon: this.isSidebarOpen ? 'PanelLeftClose' : 'PanelLeftOpen',
				action: () => this.toggleSidebar(),
			},
			{
				id: 'lightMode',
			},
		];
	}

	handleMenuItemClick(item: any): void {
		if (item.id && item.id !== 'isSidebarOpen') {
			this.navigateTo(item.id);
		}
	}

	navigateTo(section: string): void {
		this.sectionChange.emit(section);
	}

	isActive(id: string): boolean {
		return this.currentSection === id;
	}

	ngOnInit(): void {
		// Subscribe to theme changes
		this.themeService.theme$.subscribe((theme) => {
			this.currentTheme = theme as 'light' | 'dark';
		});

		// Apply the current theme
		this.themeService.setTheme(this.currentTheme);
	}

	switchTheme(theme?: 'light' | 'dark'): void {
		const newTheme = theme || (this.currentTheme === 'light' ? 'dark' : 'light');
		this.themeService.setTheme(newTheme);
	}

	isLightTheme(): boolean {
		return this.currentTheme === 'light';
	}

	isDarkTheme(): boolean {
		return this.currentTheme === 'dark';
	}

	// Methods for compatibility with unified template (responsive mode)
	get isResponsiveMode(): boolean {
		return false; // UI Kit is always desktop
	}

	closeSidebar(): void {
		// No-op for UI Kit
	}

	openSidebar(): void {
		// No-op for UI Kit
	}

	toggleProfileMenu(): void {
		this.isProfileMenuOpen = !this.isProfileMenuOpen;
	}

	closeProfileMenu(): void {
		this.isProfileMenuOpen = false;
	}

	handleProfileMenuItemClick(_item: any): void {
		// No-op for UI Kit
	}

	navigateToProfile(): void {
		// No-op for UI Kit
	}

	getUserAvatar(): string {
		return this.currentUser?.avatarUrl || 'assets/layout/images/sticker.png';
	}

	getUserEmployeeId(): number | undefined {
		return this.currentEmployeeId;
	}

	handleLogout(): void {
		// No-op for UI Kit (this is just for demo)
	}

	scrollToItem(_elementRef: ElementRef): void {
		// No-op for UI Kit - always in desktop mode
	}
}
